import { z } from 'zod';
import { Timestamp } from 'firebase/firestore';

// Base validation schemas
export const emailSchema = z
  .string()
  .min(1, 'Email is required')
  .email('Invalid email format')
  .transform(email => email.toLowerCase().trim());

export const phoneSchema = z
  .string()
  .min(1, 'Phone number is required')
  .regex(/^05\d{8}$/, 'Phone number must be in format 05XXXXXXXX');

// Schema that accepts either email or phone
export const emailOrPhoneSchema = z
  .string()
  .min(1, 'Email or phone number is required')
  .refine((value) => {
    // Check if it's a valid email
    const emailResult = z.string().email().safeParse(value);
    if (emailResult.success) return true;
    
    // Check if it's a valid phone number
    const phoneResult = z.string().regex(/^05\d{8}$/).safeParse(value);
    return phoneResult.success;
  }, 'Please enter a valid email address or phone number (05XXXXXXXX)')
  .transform(value => {
    // If it's an email, lowercase and trim it
    if (value.includes('@')) {
      return value.toLowerCase().trim();
    }
    // If it's a phone number, return as is
    return value;
  });

export const passwordSchema = z
  .string()
  .min(6, 'Password must be at least 6 characters')
  .max(128, 'Password must be less than 128 characters');

export const nameSchema = z
  .string()
  .min(1, 'Name is required')
  .max(50, 'Name must be less than 50 characters')
  .trim();

// Role schemas
export const roleSchema = z.enum([
  'admin',
  'customer', 
  'coach',
  'examiner',
  'manager',
  'partner',
  'volunteer',
  'instructor',
  'organizer',
  'provider',
  'coordinator',
  'audit',
  'marketer'
]);

// Handle both Date and Timestamp types
const dateSchema = z.union([
  z.date(),
  z.instanceof(Timestamp).transform(ts => ts.toDate())
]);

export const roleAssignmentSchema = z.object({
  role: roleSchema,
  assignedAt: z.date(),
  assignedBy: z.string(),
  isActive: z.boolean()
});

// Login schema - now accepts email or phone
export const loginSchema = z.object({
  email: emailOrPhoneSchema,
  password: passwordSchema
});

// Create user schema
export const createUserSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  firstNameEn: nameSchema,
  lastNameEn: nameSchema,
  firstNameAr: nameSchema,
  lastNameAr: nameSchema,
  phone: phoneSchema,
  role: roleSchema.default('customer'),
  isVolunteer: z.boolean().optional().default(false)
});

// User schema
export const userSchema = z.object({
  id: z.string(),
  // Optional, like `phone` below. This app is phone-first: an account created by
  // an admin, or through phone-OTP signup, has no email field at all. Requiring
  // it here rejected those users at LOGIN with a bare zod "Required", because
  // getUserData spreads the raw document straight into userSchema.parse.
  email: emailSchema.optional().or(z.literal('')).or(z.null()),
  firstNameEn: nameSchema,
  lastNameEn: nameSchema,
  firstNameAr: nameSchema,
  lastNameAr: nameSchema,
  phone: phoneSchema.optional().or(z.literal('')).or(z.null()),
  photoURL: z.string().optional().default(''),
  roles: z.array(roleAssignmentSchema),
  createdAt: z.union([z.date(), z.custom<Timestamp>()]),
  updatedAt: z.union([z.date(), z.custom<Timestamp>()])
});

// Export types
export type LoginInput = z.infer<typeof loginSchema>;
export type CreateUserInput = z.infer<typeof createUserSchema>;
export type UserValidation = z.infer<typeof userSchema>;